// === FEATURE: Cassette Deck — generative tape engine ===
// A tiny, dependency-free WebAudio engine that SYNTHESIZES every sound it makes
// in-code. It reuses NO audio files: there are no <audio> elements, no fetches,
// no jukehost tracks. Each "tape" is a different generative soundscape built
// from oscillators, filtered noise and slow LFOs, all coloured by a shared
// "tape path" — wow & flutter (slow + fast pitch wobble via a DelayNode whose
// time is modulated), a warm lowpass + gentle highpass (the cassette's limited
// bandwidth), and a constant bed of tape hiss + a faint motor/transport hum.
//
// Character: this is a MACHINE playing degraded, half-remembered recordings —
// evolving drones and distant found-recording textures. It is deliberately
// distinct from StaticFM (radio static / glossolalia) and the MusicBumper
// (actual songs): no melody, no station-tuning, just slow analog atmosphere.
//
// Public surface (all null-safe, all best-effort):
//   const eng = createTapeEngine();
//   eng.play(tapeIndex)   build (lazily) + start the given tape, fade in
//   eng.stop()            fade out + stop, free the per-tape nodes
//   eng.getLevel()        current overall loudness 0..1 (for audioBus + VU)
//   eng.dispose()         tear everything down, close the AudioContext
//   eng.tapeCount         number of available tapes
//   eng.tapeName(i)       label for tape i
//
// Everything is wrapped defensively so a blocked/again-suspended AudioContext
// never throws into React; in the worst case the engine is simply silent and
// getLevel() reports 0 so the reels/VU just rest.

const TAU = Math.PI * 2;

// The tapes. Each has a label and a builder(ctx, busInput) that wires its own
// little graph INTO busInput (the head of the shared tape path) and returns a
// { start(t), stop(t), cleanup() } controller. Builders never touch the
// destination directly — the shared path does the tape colouring + output.
const TAPES = [
  {
    name: "DERELICT HOTEL",
    build: buildDerelictHotel,
  },
  {
    name: "TIDE / MAGNET",
    build: buildTideMagnet,
  },
  {
    name: "ENGINE ROOM",
    build: buildEngineRoom,
  },
  {
    name: "DISTANT CHOIR",
    build: buildDistantChoir,
  },
];

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------

// A looping AudioBufferSourceNode of white noise. One short buffer, looped, is
// far cheaper than a ScriptProcessor and never garbage-stutters.
function makeNoise(ctx, seconds = 2) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

// Brown-ish noise buffer (integrated white) — darker, "wind / room tone".
function makeBrownNoise(ctx, seconds = 3) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    last = (last + 0.02 * w) / 1.02;
    d[i] = last * 3.5;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

// A slow sine LFO (oscillator) feeding a gain, used to modulate any param.
// Returns { osc, depth } already started-capable; caller connects depth to the
// AudioParam and calls osc.start().
function makeLfo(ctx, rateHz, depth) {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.value = rateHz;
  const g = ctx.createGain();
  g.gain.value = depth;
  osc.connect(g);
  return { osc, depth: g };
}

// ---------------------------------------------------------------------------
// TAPE 1 — DERELICT HOTEL
// A slow, detuned chord drone (a dim, sustained organ-ish pad) with a far-off
// filtered-noise "room" swell that breathes in and out. Felt like an empty
// ballroom remembered through worn tape.
// ---------------------------------------------------------------------------
function buildDerelictHotel(ctx, busInput) {
  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(busInput);

  // Detuned drone stack — a minor-ish cluster, deliberately not a clean chord.
  const freqs = [110, 130.8, 164.8, 220, 246.9];
  const oscs = [];
  const droneGain = ctx.createGain();
  droneGain.gain.value = 0.12;
  // Slow filter swell on the drone so it "breathes".
  const droneLp = ctx.createBiquadFilter();
  droneLp.type = "lowpass";
  droneLp.frequency.value = 700;
  droneLp.Q.value = 0.6;
  const breath = makeLfo(ctx, 0.05, 420); // +/- 420Hz cutoff sweep, ~20s period
  breath.depth.connect(droneLp.frequency);
  droneLp.connect(droneGain);
  droneGain.connect(out);

  freqs.forEach((f, i) => {
    const o = ctx.createOscillator();
    o.type = i % 2 ? "sine" : "triangle";
    o.frequency.value = f;
    o.detune.value = (i - 2) * 4; // a few cents apart -> slow beating
    const g = ctx.createGain();
    g.gain.value = i === 0 ? 0.5 : 0.3 / i;
    o.connect(g);
    g.connect(droneLp);
    oscs.push(o);
  });

  // Distant "room" swell — brown noise through a resonant bandpass that drifts.
  const room = makeBrownNoise(ctx, 3);
  const roomBp = ctx.createBiquadFilter();
  roomBp.type = "bandpass";
  roomBp.frequency.value = 520;
  roomBp.Q.value = 4;
  const roomGain = ctx.createGain();
  roomGain.gain.value = 0.0;
  const swell = makeLfo(ctx, 0.033, 0.05); // very slow amplitude swell
  swell.depth.connect(roomGain.gain);
  const roomDrift = makeLfo(ctx, 0.021, 240);
  roomDrift.depth.connect(roomBp.frequency);
  room.connect(roomBp);
  roomBp.connect(roomGain);
  roomGain.connect(out);

  return {
    start(t) {
      oscs.forEach((o) => o.start(t));
      [breath, swell, roomDrift].forEach((l) => l.osc.start(t));
      room.start(t);
      out.gain.setTargetAtTime(0.9, t, 1.5);
    },
    stop(t) {
      out.gain.setTargetAtTime(0, t, 0.4);
      const end = t + 1.2;
      oscs.forEach((o) => safeStop(o, end));
      [breath, swell, roomDrift].forEach((l) => safeStop(l.osc, end));
      safeStop(room, end);
    },
    cleanup() {
      try { out.disconnect(); } catch (e) {}
    },
  };
}

// ---------------------------------------------------------------------------
// TAPE 2 — TIDE / MAGNET
// A surging filtered-noise "sea" with a deep sub pulse like a buoy, and a thin
// metallic shimmer that comes and goes — magnetic, oceanic, cold.
// ---------------------------------------------------------------------------
function buildTideMagnet(ctx, busInput) {
  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(busInput);

  // The "tide": white noise -> lowpass whose cutoff surges up and back down.
  const sea = makeNoise(ctx, 2);
  const seaLp = ctx.createBiquadFilter();
  seaLp.type = "lowpass";
  seaLp.frequency.value = 500;
  seaLp.Q.value = 0.8;
  const seaGain = ctx.createGain();
  seaGain.gain.value = 0.16;
  const surge = makeLfo(ctx, 0.08, 650); // ~12s wash in / out
  surge.depth.connect(seaLp.frequency);
  const surgeAmp = makeLfo(ctx, 0.08, 0.09);
  surgeAmp.depth.connect(seaGain.gain);
  sea.connect(seaLp);
  seaLp.connect(seaGain);
  seaGain.connect(out);

  // Deep buoy pulse — a slow sine with its own tremolo, very low.
  const sub = ctx.createOscillator();
  sub.type = "sine";
  sub.frequency.value = 47;
  const subGain = ctx.createGain();
  subGain.gain.value = 0;
  const buoy = makeLfo(ctx, 0.12, 0.07); // a soft heartbeat-ish swell
  buoy.depth.connect(subGain.gain);
  sub.connect(subGain);
  subGain.connect(out);

  // Metallic shimmer — high detuned sines through a comb-ish short delay.
  const shimmer = ctx.createGain();
  shimmer.gain.value = 0;
  const shA = ctx.createOscillator();
  shA.type = "sine";
  shA.frequency.value = 1840;
  const shB = ctx.createOscillator();
  shB.type = "sine";
  shB.frequency.value = 1847; // ~7Hz beat
  const shimDelay = ctx.createDelay();
  shimDelay.delayTime.value = 0.012;
  const shimFb = ctx.createGain();
  shimFb.gain.value = 0.5;
  shA.connect(shimmer);
  shB.connect(shimmer);
  shimmer.connect(shimDelay);
  shimDelay.connect(shimFb);
  shimFb.connect(shimDelay);
  const shimOut = ctx.createGain();
  shimOut.gain.value = 0.05;
  shimDelay.connect(shimOut);
  shimmer.connect(shimOut);
  shimOut.connect(out);
  const shimSwell = makeLfo(ctx, 0.017, 0.5);
  shimSwell.depth.connect(shimmer.gain);

  return {
    start(t) {
      sea.start(t);
      sub.start(t);
      shA.start(t);
      shB.start(t);
      [surge, surgeAmp, buoy, shimSwell].forEach((l) => l.osc.start(t));
      out.gain.setTargetAtTime(0.95, t, 1.8);
    },
    stop(t) {
      out.gain.setTargetAtTime(0, t, 0.4);
      const end = t + 1.2;
      safeStop(sea, end);
      safeStop(sub, end);
      safeStop(shA, end);
      safeStop(shB, end);
      [surge, surgeAmp, buoy, shimSwell].forEach((l) => safeStop(l.osc, end));
    },
    cleanup() {
      try { out.disconnect(); } catch (e) {}
    },
  };
}

// ---------------------------------------------------------------------------
// TAPE 3 — ENGINE ROOM
// Rhythmic industrial machine atmosphere: a low rumbling motor (detuned saws
// through a heavy lowpass), a clanking pulse from gated noise, and a high
// pressure-hiss that ebbs. Mechanical, claustrophobic, alive.
// ---------------------------------------------------------------------------
function buildEngineRoom(ctx, busInput) {
  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(busInput);

  // Motor rumble — two slightly detuned saws very low, heavy lowpass, slow
  // wobble in pitch so it "labours".
  const m1 = ctx.createOscillator();
  m1.type = "sawtooth";
  m1.frequency.value = 41;
  const m2 = ctx.createOscillator();
  m2.type = "sawtooth";
  m2.frequency.value = 41.5;
  const motorLp = ctx.createBiquadFilter();
  motorLp.type = "lowpass";
  motorLp.frequency.value = 220;
  motorLp.Q.value = 1.2;
  const motorGain = ctx.createGain();
  motorGain.gain.value = 0.22;
  const labour = makeLfo(ctx, 0.7, 3.5); // small RPM wobble
  labour.depth.connect(m1.frequency);
  labour.depth.connect(m2.frequency);
  m1.connect(motorLp);
  m2.connect(motorLp);
  motorLp.connect(motorGain);
  motorGain.connect(out);

  // Clank pulse — noise gated by a fast LFO into a resonant bandpass.
  const clank = makeNoise(ctx, 2);
  const clankBp = ctx.createBiquadFilter();
  clankBp.type = "bandpass";
  clankBp.frequency.value = 380;
  clankBp.Q.value = 8;
  const clankGate = ctx.createGain();
  clankGate.gain.value = 0;
  // A ~1.2Hz "chug": square-ish via a sine that we bias toward 0 most of the
  // time using a low depth + the gate's own floor handled by the LFO offset.
  const chug = makeLfo(ctx, 1.2, 0.16);
  chug.depth.connect(clankGate.gain);
  clank.connect(clankBp);
  clankBp.connect(clankGate);
  clankGate.connect(out);

  // Pressure hiss — bright noise that ebbs slowly, like venting steam.
  const hiss = makeNoise(ctx, 2);
  const hissHp = ctx.createBiquadFilter();
  hissHp.type = "highpass";
  hissHp.frequency.value = 3500;
  const hissGain = ctx.createGain();
  hissGain.gain.value = 0;
  const vent = makeLfo(ctx, 0.045, 0.05);
  vent.depth.connect(hissGain.gain);
  hiss.connect(hissHp);
  hissHp.connect(hissGain);
  hissGain.connect(out);

  return {
    start(t) {
      m1.start(t);
      m2.start(t);
      clank.start(t);
      hiss.start(t);
      [labour, chug, vent].forEach((l) => l.osc.start(t));
      out.gain.setTargetAtTime(0.9, t, 1.2);
    },
    stop(t) {
      out.gain.setTargetAtTime(0, t, 0.4);
      const end = t + 1.2;
      [m1, m2, clank, hiss].forEach((n) => safeStop(n, end));
      [labour, chug, vent].forEach((l) => safeStop(l.osc, end));
    },
    cleanup() {
      try { out.disconnect(); } catch (e) {}
    },
  };
}

// ---------------------------------------------------------------------------
// TAPE 4 — DISTANT CHOIR
// A ghostly "voice" pad: formant-filtered sawtooth that drifts between vowel
// shapes, octave-stacked, with heavy wow so it sounds like a warped tape of a
// recital. Eerie, sweet, wrong.
// ---------------------------------------------------------------------------
function buildDistantChoir(ctx, busInput) {
  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(busInput);

  // The "voice" — stacked saws an octave + fifth apart for a choir thickness.
  const voiceGain = ctx.createGain();
  voiceGain.gain.value = 0.1;
  const partials = [196, 294, 392]; // G3, D4, G4
  const voices = [];
  partials.forEach((f, i) => {
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = f;
    o.detune.value = (Math.random() - 0.5) * 14;
    const g = ctx.createGain();
    g.gain.value = 0.5 / (i + 1);
    o.connect(g);
    voices.push({ o, g });
  });

  // Two formant bandpass filters drifting between vowels -> the "aah/ooh".
  const f1 = ctx.createBiquadFilter();
  f1.type = "bandpass";
  f1.frequency.value = 700;
  f1.Q.value = 6;
  const f2 = ctx.createBiquadFilter();
  f2.type = "bandpass";
  f2.frequency.value = 1100;
  f2.Q.value = 8;
  const vowel1 = makeLfo(ctx, 0.06, 240);
  vowel1.depth.connect(f1.frequency);
  const vowel2 = makeLfo(ctx, 0.043, 500);
  vowel2.depth.connect(f2.frequency);

  voices.forEach((v) => {
    v.g.connect(f1);
    v.g.connect(f2);
  });
  f1.connect(voiceGain);
  f2.connect(voiceGain);
  voiceGain.connect(out);

  // Slow swell so phrases "breathe" in and out like held notes.
  const phrase = makeLfo(ctx, 0.07, 0.06);
  phrase.depth.connect(voiceGain.gain);

  return {
    start(t) {
      voices.forEach((v) => v.o.start(t));
      [vowel1, vowel2, phrase].forEach((l) => l.osc.start(t));
      out.gain.setTargetAtTime(0.85, t, 2.0);
    },
    stop(t) {
      out.gain.setTargetAtTime(0, t, 0.5);
      const end = t + 1.4;
      voices.forEach((v) => safeStop(v.o, end));
      [vowel1, vowel2, phrase].forEach((l) => safeStop(l.osc, end));
    },
    cleanup() {
      try { out.disconnect(); } catch (e) {}
    },
  };
}

// Stop a node at time t, swallowing the "already stopped / not started" throw.
function safeStop(node, t) {
  try { node.stop(t); } catch (e) {}
}

// ---------------------------------------------------------------------------
// The engine: shared tape path + per-tape lifecycle + level metering.
// ---------------------------------------------------------------------------
export function createTapeEngine() {
  let ctx = null;
  let started = false; // whether a tape is currently sounding
  let current = null; // active tape controller
  let analyser = null;
  let levelData = null;
  let path = null; // { input, wow, hiss, hum } persistent tape colouring

  function ensureCtx() {
    if (ctx) return ctx;
    if (typeof window === "undefined") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch (e) {
      ctx = null;
      return null;
    }
    buildPath();
    return ctx;
  }

  // The persistent "tape path": every tape feeds path.input. From there:
  //   input -> wowDelay (modulated delay = wow & flutter) -> warmLp -> thinHp
  //         -> mix -> analyser -> master -> destination
  // plus a constant hiss + transport hum mixed in at `mix` so the machine is
  // never truly silent while running.
  function buildPath() {
    const input = ctx.createGain();
    input.gain.value = 1;

    // Wow & flutter: a short delay whose time is modulated by a slow LFO (wow)
    // plus a faster, shallower LFO (flutter) -> pitch warble.
    const wowDelay = ctx.createDelay();
    wowDelay.delayTime.value = 0.006;
    const wow = makeLfo(ctx, 0.6, 0.0016); // slow
    const flutter = makeLfo(ctx, 6.5, 0.0004); // fast, shallow
    wow.depth.connect(wowDelay.delayTime);
    flutter.depth.connect(wowDelay.delayTime);
    input.connect(wowDelay);

    // Warm bandwidth: lowpass (lose the highs) + gentle highpass (lose deep sub
    // rumble) — the classic worn-cassette window.
    const warmLp = ctx.createBiquadFilter();
    warmLp.type = "lowpass";
    warmLp.frequency.value = 4200;
    warmLp.Q.value = 0.4;
    const thinHp = ctx.createBiquadFilter();
    thinHp.type = "highpass";
    thinHp.frequency.value = 40;
    wowDelay.connect(warmLp);
    warmLp.connect(thinHp);

    const mix = ctx.createGain();
    mix.gain.value = 1;
    thinHp.connect(mix);

    // Constant tape hiss bed.
    const hissSrc = makeNoise(ctx, 2);
    const hissHp = ctx.createBiquadFilter();
    hissHp.type = "highpass";
    hissHp.frequency.value = 5200;
    const hissGain = ctx.createGain();
    hissGain.gain.value = 0.012;
    hissSrc.connect(hissHp);
    hissHp.connect(hissGain);
    hissGain.connect(mix);

    // Faint transport / motor hum (50Hz + a touch of 100Hz).
    const hum1 = ctx.createOscillator();
    hum1.type = "sine";
    hum1.frequency.value = 50;
    const hum2 = ctx.createOscillator();
    hum2.type = "sine";
    hum2.frequency.value = 100;
    const humGain = ctx.createGain();
    humGain.gain.value = 0.01;
    hum1.connect(humGain);
    hum2.connect(humGain);
    humGain.connect(mix);

    // Metering tap + master out.
    analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;
    levelData = new Uint8Array(analyser.frequencyBinCount);
    const master = ctx.createGain();
    master.gain.value = 0.9;
    mix.connect(analyser);
    analyser.connect(master);
    master.connect(ctx.destination);

    // Start the always-on bits now (they're inaudible until a tape plays only
    // because the hiss/hum are very quiet; that's intentional machine presence).
    const t0 = ctx.currentTime;
    [wow, flutter].forEach((l) => l.osc.start(t0));
    hissSrc.start(t0);
    hum1.start(t0);
    hum2.start(t0);

    path = { input, master };
  }

  // Resume a suspended context (autoplay policy) — best effort.
  function resume() {
    if (ctx && ctx.state === "suspended" && ctx.resume) {
      ctx.resume().catch(() => {});
    }
  }

  function play(index) {
    const c = ensureCtx();
    if (!c) return;
    resume();
    // Replace any current tape (defensive — UI normally stops first).
    if (current) {
      try { current.stop(c.currentTime); } catch (e) {}
      try { current.cleanup(); } catch (e) {}
      current = null;
    }
    const tape = TAPES[((index % TAPES.length) + TAPES.length) % TAPES.length];
    try {
      current = tape.build(c, path.input);
      current.start(c.currentTime + 0.02);
      started = true;
    } catch (e) {
      current = null;
      started = false;
    }
  }

  function stop() {
    started = false;
    if (!ctx || !current) {
      current = null;
      return;
    }
    const c = current;
    const t = ctx.currentTime;
    try { c.stop(t); } catch (e) {}
    current = null;
    // Free the tape's nodes a little after its fade-out completes.
    setTimeout(() => {
      try { c.cleanup(); } catch (e) {}
    }, 1600);
  }

  function getLevel() {
    if (!analyser || !levelData || !started) return 0;
    resume();
    try {
      analyser.getByteFrequencyData(levelData);
    } catch (e) {
      return 0;
    }
    const n = levelData.length;
    if (!n) return 0;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += levelData[i];
    return sum / n / 255;
  }

  function dispose() {
    started = false;
    if (current) {
      try { current.stop(ctx ? ctx.currentTime : 0); } catch (e) {}
      try { current.cleanup(); } catch (e) {}
      current = null;
    }
    const c = ctx;
    ctx = null;
    analyser = null;
    levelData = null;
    path = null;
    if (c && c.close) c.close().catch(() => {});
  }

  return {
    play,
    stop,
    getLevel,
    dispose,
    tapeCount: TAPES.length,
    tapeName: (i) => {
      const t = TAPES[((i % TAPES.length) + TAPES.length) % TAPES.length];
      return t ? t.name : "";
    },
  };
}

export default createTapeEngine;
